import {
  DEFAULT_IMAGE_MARK_WIDTH,
  clampMarkBox,
  imageMarkBox,
  isImageMark,
  resolveSlotText,
  textMarkLayout,
  toCalendarDate,
  type MarkBox,
  type StampSlot,
} from '@provenance/shared';
import type { PixelSize, TextMeasurer } from './measure';

/**
 * Where each mark sits on the canvas, and how moving or resizing it changes the slot.
 *
 * Pure functions with no React around them, because this is the arithmetic that decides
 * whether a chamber's seal lands on the signature block or halfway up the page.
 *
 * ── The two coordinate systems ───────────────────────────────────────────────
 *
 *   SCREEN   CSS pixels, origin TOP-left, y increases downward.
 *   PDF      points (72/inch), origin BOTTOM-left, y increases upward.
 *
 * A mark's BOX comes from `@provenance/shared`'s mark-box functions — the same ones the
 * stamping engine draws with (T-125). This file only turns boxes into pixels and pointer
 * movements back into slot values; it never decides a size of its own.
 * ────────────────────────────────────────────────────────────────────────────
 */

export interface PageGeometry {
  /** True page size in PDF points. */
  pointWidth: number;
  pointHeight: number;
  /** Rendered CSS pixels per PDF point. */
  scale: number;
}

/** What the proof resolves text from, sent by the server so the two cannot disagree. */
export interface SampleInputs {
  certificateNo: string;
  /** A calendar date, `YYYY-MM-DD`. */
  certificateDate: string;
  signatoryName: string;
  designation: string;
  issuedAtText: string;
  /** Shown for a fixed-text mark with nothing typed yet — the proof does the same. */
  freeTextPlaceholder: string;
}

export interface MarkContext {
  page: { width: number; height: number };
  /** The image a seal or signature mark prints, or null when it would print nothing. */
  imageUrl: (slot: StampSlot) => string | null;
  imageSizes: Record<string, PixelSize>;
  measure: TextMeasurer | null;
  sample: SampleInputs;
}

export interface MarkLayout {
  /** Where the mark is drawn — clamped into the page for images, exactly as the engine does. */
  box: MarkBox;
  /** Before clamping. Moves and resizes are computed from this, so a nudge is never lost. */
  raw: MarkBox;
  /** For text: where the string starts, and the string itself. */
  drawX?: number;
  text?: string;
  textWidth?: number;
  /** For images: the URL drawn. Null means the mark would print nothing. */
  imageUrl?: string | null;
  /**
   * True while the box is provisional — the image or the font metrics are still loading —
   * or when there is nothing to measure. Never compared against the proof.
   */
  provisional: boolean;
}

/** What a text mark prints, resolved the way the proof resolves it. */
export function slotText(slot: StampSlot, sample: SampleInputs): string | null {
  const text = resolveSlotText(slot.slotType, {
    certificateNo: sample.certificateNo,
    certificateDate: toCalendarDate(sample.certificateDate) ?? new Date(),
    dateFormat: slot.dateFormat,
    signatoryName: sample.signatoryName,
    designation: sample.designation,
    issuedAtText: sample.issuedAtText,
    staticText: slot.staticText ?? null,
  });
  return text?.trim() || (slot.slotType === 'FREE_TEXT' ? sample.freeTextPlaceholder : null);
}

export function markLayout(slot: StampSlot, ctx: MarkContext): MarkLayout {
  if (isImageMark(slot.slotType)) {
    const imageUrl = ctx.imageUrl(slot);
    const size = imageUrl ? ctx.imageSizes[imageUrl] : undefined;
    // Until the image has loaded (or when there is none) the mark is shown at its saved
    // width with a stand-in height — clearly provisional, never measured against the proof.
    const raw = size
      ? imageMarkBox(slot, size)
      : {
          x: slot.x,
          y: slot.y,
          width: slot.width ?? DEFAULT_IMAGE_MARK_WIDTH,
          height: slot.height ?? (slot.width ?? DEFAULT_IMAGE_MARK_WIDTH) * 0.5,
        };
    return { raw, box: clampMarkBox(raw, ctx.page), imageUrl, provisional: !size };
  }

  const text = slotText(slot, ctx.sample) ?? '';
  const measured = ctx.measure
    ? ctx.measure(text, slot.fontFamily, slot.fontSize)
    : { width: Math.max(24, text.length * slot.fontSize * 0.5), height: slot.fontSize * 1.15, descent: slot.fontSize * 0.22 };
  const layout = textMarkLayout(slot, measured);
  // Text is not clamped by the engine, so it is not clamped here either.
  return {
    raw: layout.box,
    box: layout.box,
    drawX: layout.drawX,
    text,
    textWidth: measured.width,
    provisional: !ctx.measure || !text,
  };
}

/** A box in PDF points → a CSS rectangle within the rendered page. */
export function boxToScreen(box: MarkBox, geometry: PageGeometry) {
  return {
    left: box.x * geometry.scale,
    top: (geometry.pointHeight - box.y - box.height) * geometry.scale,
    width: box.width * geometry.scale,
    height: box.height * geometry.scale,
  };
}

/**
 * The slot's new x/y after its box moves by (dx, dy) points.
 *
 * Works on the box, not the slot: a text mark's x is its anchor — the left edge, the
 * centre or the right edge — so moving it means moving the box and shifting the anchor by
 * the same amount. The box is kept inside the page.
 */
export function movedSlot(
  slot: StampSlot,
  layout: MarkLayout,
  dx: number,
  dy: number,
  page: { width: number; height: number },
): { x: number; y: number } {
  const target = clampMarkBox({ ...layout.raw, x: layout.raw.x + dx, y: layout.raw.y + dy }, page);
  return { x: round(slot.x + (target.x - layout.raw.x)), y: round(slot.y + (target.y - layout.raw.y)) };
}

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'e' | 'w';

/** The smallest a mark can be dragged to, in points. Small enough for any real mark. */
const MIN_SIZE = 12;

/**
 * The slot values after dragging a resize handle to `pointer` (PDF points).
 *
 * IMAGES keep their proportions, always. The opposite corner stays put and the width
 * follows the pointer; height is left unset so the engine derives it from the image, as it
 * does for every seal. A seal squashed to fit a box stops looking like the chamber's seal.
 *
 * TEXT resizes sideways only, and gains a box width: its x becomes the box's left edge and
 * its alignment applies inside the box (see `textMarkLayout`).
 */
export function resizedSlot(
  slot: StampSlot,
  layout: MarkLayout,
  handle: ResizeHandle,
  pointer: { x: number; y: number },
): Partial<StampSlot> {
  const { raw } = layout;
  const left = raw.x;
  const right = raw.x + raw.width;
  const bottom = raw.y;
  const top = raw.y + raw.height;
  const west = handle.includes('w');

  if (isImageMark(slot.slotType)) {
    const ratio = raw.width > 0 ? raw.height / raw.width : 1;
    const anchorX = west ? right : left;
    const anchorY = handle.startsWith('s') ? top : bottom;
    // Whichever direction the pointer moved further decides the size, so dragging a
    // corner straight up works as well as dragging it sideways.
    const byWidth = Math.abs(pointer.x - anchorX);
    const byHeight = Math.abs(pointer.y - anchorY) / ratio;
    const width = Math.max(MIN_SIZE, byWidth, byHeight);
    const height = width * ratio;
    return {
      x: round(west ? right - width : left),
      y: round(handle.startsWith('s') ? top - height : bottom),
      width: round(width),
      height: null,
    };
  }

  const width = Math.max(MIN_SIZE, west ? right - pointer.x : pointer.x - left);
  return { x: round(west ? right - width : left), width: round(width) };
}

/**
 * Takes a text mark's box width away, keeping the text where it is.
 *
 * Without a width, x is an anchor rather than a box edge, so it has to be moved to where
 * the anchor now sits — otherwise a centred date would jump half its width sideways.
 */
export function fitTextToContent(slot: StampSlot, layout: MarkLayout): Partial<StampSlot> {
  const drawX = layout.drawX ?? slot.x;
  const width = layout.textWidth ?? 0;
  const x = slot.align === 'CENTER' ? drawX + width / 2 : slot.align === 'RIGHT' ? drawX + width : drawX;
  return { x: round(x), width: null };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
